/**
 * Environment configuration
 *
 * This file centralizes all environment-specific configuration to make it
 * easier to manage different environments (development, testing, production)
 */

// Get environment variables from runtime config or Vite
const getEnvVar = (key: string, defaultValue: string) => {
  // Check if runtime config is available (injected by start.sh)
  if (typeof window !== "undefined" && (window as any).ENV_CONFIG) {
    return (window as any).ENV_CONFIG[key] || defaultValue;
  }
  // Fallback to Vite environment variables
  return import.meta.env[key] || defaultValue;
};

// Default to development values
const defaultConfig = {
  // App branding
  app: {
    name: getEnvVar("VITE_APP_NAME", "Heighten Management"),
    tagline: getEnvVar("VITE_APP_TAGLINE", "Restaurant Staff Portal"),
  },

  // API configuration
  api: {
    url: getEnvVar("VITE_API_URL", "http://localhost:3030"),
    trpcEndpoint: getEnvVar("VITE_TRPC_ENDPOINT", "/trpc"),
    timeout: 10000, // 10 seconds
  },

  // Authentication configuration
  auth: {
    tokenKey: "auth_token",
    refreshTokenKey: "refresh_token",
    tokenExpiry: parseInt(getEnvVar("VITE_AUTH_TOKEN_EXPIRY", "3600000")), // 1 hour in milliseconds
    mfaSessionDuration: parseInt(
      getEnvVar("VITE_MFA_SESSION_DURATION", "300000"),
    ), // 5 minutes in milliseconds
  },

  // Feature flags
  features: {
    enableMfa: true,
    enablePasswordReset: true,
  },
};

// Environment-specific overrides
const environmentConfig: Record<string, Partial<typeof defaultConfig>> = {
  development: {
    // Development uses the default config with environment variables
  },
  test: {
    // Test-specific configuration
    api: {
      ...defaultConfig.api,
      url: "http://localhost:3031", // Use a different port for testing
    },
  },
  production: {
    // Production uses environment variables as defaults are set
  },
};

// Determine current environment
const environment = import.meta.env.MODE || "development";

// Create the final configuration by merging default with environment-specific
const config = {
  ...defaultConfig,
  ...(environmentConfig[environment] || {}),
  environment,
};

// Log configuration in development for debugging
if (environment === "development") {
  console.log("🔧 Frontend Configuration:", {
    environment,
    apiUrl: config.api.url,
    trpcEndpoint: config.api.trpcEndpoint,
    fullTrpcUrl: `${config.api.url}${config.api.trpcEndpoint}`,
  });
}

export default config;
